import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './useAuth';

const CONNECTION_STATES = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
  ERROR: 'ERROR'
};

const DEFAULT_OPTIONS = {
  reconnectAttempts: 5,
  reconnectInterval: 3000,
  reconnectBackoffRate: 1.5,
  maxReconnectInterval: 30000,
  heartbeatInterval: 30000,
  connectionTimeout: 10000,
  enableHeartbeat: true,
  enableReconnect: true,
  enableLogging: true
};

const useWebSocket = (url, options = {}) => {
  const { user, isAuthenticated, getAccessToken } = useAuth();
  const config = { ...DEFAULT_OPTIONS, ...options };
  
  // State
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [messageHistory, setMessageHistory] = useState([]);
  const [latency, setLatency] = useState(null);

  // Refs
  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const lastPingTimeRef = useRef(null);
  const messageQueueRef = useRef([]);
  const isManualCloseRef = useRef(false);

  // Computed values
  const isConnected = connectionState === CONNECTION_STATES.CONNECTED;
  const isConnecting = connectionState === CONNECTION_STATES.CONNECTING;
  const isReconnecting = connectionState === CONNECTION_STATES.RECONNECTING;

  // Logging utility
  const log = useCallback((level, message, data = null) => {
    if (!config.enableLogging) return;
    
    const timestamp = new Date().toISOString();
    const logMessage = `[WebSocket ${timestamp}] ${message}`;
    
    if (level === 'error') {
      console.error(logMessage, data);
    } else if (level === 'warn') {
      console.warn(logMessage, data);
    } else {
      console.log(logMessage, data);
    }
  }, [config.enableLogging]);

  // Clear all timeouts
  const clearTimeouts = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  // Send heartbeat ping
  const sendHeartbeat = useCallback(() => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      lastPingTimeRef.current = Date.now();
      websocketRef.current.send(JSON.stringify({
        type: 'ping',
        timestamp: lastPingTimeRef.current
      }));
      log('debug', 'Heartbeat sent');
    }
  }, [log]);

  // Start heartbeat
  const startHeartbeat = useCallback(() => {
    if (!config.enableHeartbeat) return;
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, config.heartbeatInterval);
    log('debug', `Heartbeat started (interval: ${config.heartbeatInterval}ms)`);
  }, [config.enableHeartbeat, config.heartbeatInterval, sendHeartbeat, log]);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
      log('debug', 'Heartbeat stopped');
    }
  }, [log]);

  // Process queued messages
  const processMessageQueue = useCallback(() => {
    if (websocketRef.current?.readyState === WebSocket.OPEN && messageQueueRef.current.length > 0) {
      log('debug', `Processing ${messageQueueRef.current.length} queued messages`);
      
      while (messageQueueRef.current.length > 0) {
        const message = messageQueueRef.current.shift();
        websocketRef.current.send(message);
      }
    }
  }, [log]);

  // Handle WebSocket open
  const handleOpen = useCallback((event) => {
    log('info', '🔗 WebSocket connected');
    
    setConnectionState(CONNECTION_STATES.CONNECTED);
    setConnectionError(null);
    setReconnectAttempt(0);
    
    // Clear connection timeout
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    
    // Start heartbeat
    startHeartbeat();
    
    // Process queued messages
    processMessageQueue();
    
    // Call user callback
    options.onOpen?.(event);
  }, [log, startHeartbeat, processMessageQueue, options]);

  // Handle WebSocket message
  const handleMessage = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      
      // Handle pong response
      if (data.type === 'pong' && lastPingTimeRef.current) {
        const currentLatency = Date.now() - lastPingTimeRef.current;
        setLatency(currentLatency);
        log('debug', `Latency: ${currentLatency}ms`);
        return;
      }
      
      // Update message history (keep last 100 messages)
      setMessageHistory(prev => {
        const newHistory = [data, ...prev].slice(0, 100);
        return newHistory;
      });
      
      setLastMessage(data);
      log('debug', 'Message received', data);
      
      // Call user callback
      options.onMessage?.(data, event);
      
    } catch (error) {
      log('error', 'Failed to parse WebSocket message', { error, rawData: event.data });
      options.onError?.(error);
    }
  }, [log, options]);

  // Handle WebSocket close
  const handleClose = useCallback((event) => {
    log('warn', `🔌 WebSocket closed (code: ${event.code}, reason: ${event.reason})`);
    
    stopHeartbeat();
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
    
    // Call user callback
    options.onClose?.(event);
    
    // Attempt reconnection if not manually closed
    if (!isManualCloseRef.current && config.enableReconnect && reconnectAttempt < config.reconnectAttempts) {
      const delay = Math.min(
        config.reconnectInterval * Math.pow(config.reconnectBackoffRate, reconnectAttempt),
        config.maxReconnectInterval
      );
      
      log('info', `🔄 Attempting reconnection ${reconnectAttempt + 1}/${config.reconnectAttempts} in ${delay}ms`);
      
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      setReconnectAttempt(prev => prev + 1);
      
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    } else if (reconnectAttempt >= config.reconnectAttempts) {
      log('error', '❌ Max reconnection attempts reached');
      setConnectionError('Maximum reconnection attempts reached');
    }
  }, [log, stopHeartbeat, options, config, reconnectAttempt]);

  // Handle WebSocket error
  const handleError = useCallback((event) => {
    log('error', '❌ WebSocket error', event);
    
    setConnectionState(CONNECTION_STATES.ERROR);
    setConnectionError('WebSocket connection error');
    
    // Call user callback
    options.onError?.(event);
  }, [log, options]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!url || !isAuthenticated) {
      log('warn', 'Cannot connect: missing URL or not authenticated');
      return;
    }

    if (websocketRef.current?.readyState === WebSocket.CONNECTING || 
        websocketRef.current?.readyState === WebSocket.OPEN) {
      log('warn', 'WebSocket already connecting or connected');
      return;
    }

    try {
      log('info', `🚀 Connecting to WebSocket: ${url}`);
      
      setConnectionState(CONNECTION_STATES.CONNECTING);
      setConnectionError(null);
      isManualCloseRef.current = false;
      
      // Add authentication token to URL
      const token = getAccessToken?.() || localStorage.getItem('access_token');
      const wsUrl = token ? `${url}?token=${token}` : url;
      
      websocketRef.current = new WebSocket(wsUrl);
      
      // Set connection timeout
      connectionTimeoutRef.current = setTimeout(() => {
        if (websocketRef.current?.readyState === WebSocket.CONNECTING) {
          log('error', 'Connection timeout');
          websocketRef.current.close();
          setConnectionError('Connection timeout');
        }
      }, config.connectionTimeout);
      
      // Attach event listeners
      websocketRef.current.addEventListener('open', handleOpen);
      websocketRef.current.addEventListener('message', handleMessage);
      websocketRef.current.addEventListener('close', handleClose);
      websocketRef.current.addEventListener('error', handleError);
      
    } catch (error) {
      log('error', 'Failed to create WebSocket connection', error);
      setConnectionState(CONNECTION_STATES.ERROR);
      setConnectionError(error.message);
    }
  }, [url, isAuthenticated, log, config.connectionTimeout, getAccessToken, handleOpen, handleMessage, handleClose, handleError]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    log('info', '🔌 Manually disconnecting WebSocket');
    
    isManualCloseRef.current = true;
    clearTimeouts();
    stopHeartbeat();
    
    if (websocketRef.current) {
      websocketRef.current.close(1000, 'Manual disconnect');
      websocketRef.current = null;
    }
    
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
    setReconnectAttempt(0);
  }, [log, clearTimeouts, stopHeartbeat]);

  // Send message
  const sendMessage = useCallback((message) => {
    const messageString = typeof message === 'string' ? message : JSON.stringify(message);
    
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.send(messageString);
      log('debug', 'Message sent', message);
      return true;
    } else {
      // Queue message for later sending
      messageQueueRef.current.push(messageString);
      log('debug', 'Message queued (connection not ready)', message);
      return false;
    }
  }, [log]);

  // Reconnect manually
  const reconnect = useCallback(() => {
    log('info', '🔄 Manual reconnection requested');
    setReconnectAttempt(0);
    disconnect();
    setTimeout(connect, 1000);
  }, [log, disconnect, connect]);

  // Get connection statistics
  const getStats = useCallback(() => {
    return {
      connectionState,
      isConnected,
      isConnecting,
      isReconnecting,
      reconnectAttempt,
      messageHistory: messageHistory.slice(0, 10), // Last 10 messages
      latency,
      queuedMessages: messageQueueRef.current.length,
      uptime: isConnected ? Date.now() - (websocketRef.current?.connectedAt || Date.now()) : 0
    };
  }, [connectionState, isConnected, isConnecting, isReconnecting, reconnectAttempt, messageHistory, latency]);

  // Auto-connect when authenticated and URL is available
  useEffect(() => {
    if (url && isAuthenticated && user && connectionState === CONNECTION_STATES.DISCONNECTED) {
      const timer = setTimeout(() => {
        connect();
      }, 100); // Small delay to ensure auth is fully ready
      
      return () => clearTimeout(timer);
    }
  }, [url, isAuthenticated, user, connectionState, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isManualCloseRef.current = true;
      clearTimeouts();
      stopHeartbeat();
      
      if (websocketRef.current) {
        websocketRef.current.close(1000, 'Component unmount');
      }
    };
  }, [clearTimeouts, stopHeartbeat]);

  // Handle authentication changes
  useEffect(() => {
    if (!isAuthenticated && isConnected) {
      log('warn', 'User logged out, disconnecting WebSocket');
      disconnect();
    }
  }, [isAuthenticated, isConnected, disconnect, log]);

  // Handle visibility change (pause/resume connection)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && connectionState === CONNECTION_STATES.DISCONNECTED) {
        log('info', 'Tab became visible, attempting to reconnect');
        connect();
      } else if (document.visibilityState === 'hidden' && config.pauseOnHidden) {
        log('info', 'Tab became hidden, pausing connection');
        disconnect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connectionState, connect, disconnect, config.pauseOnHidden, log]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => {
      log('info', 'Network came online, attempting to reconnect');
      if (connectionState === CONNECTION_STATES.DISCONNECTED) {
        connect();
      }
    };

    const handleOffline = () => {
      log('warn', 'Network went offline');
      if (isConnected) {
        disconnect();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connectionState, isConnected, connect, disconnect, log]);

  return {
    // Connection state
    connectionState,
    isConnected,
    isConnecting,
    isReconnecting,
    connectionError,
    reconnectAttempt,
    
    // Data
    lastMessage,
    messageHistory,
    latency,
    
    // Actions
    connect,
    disconnect,
    reconnect,
    sendMessage,
    
    // Utilities
    getStats,
    clearError: () => setConnectionError(null),
    clearHistory: () => setMessageHistory([])
  };
};

export default useWebSocket;